import { useState } from 'react';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { formatCep } from '@dts/shared';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useClientAddressesHooks } from './hooks';
import { ClientAddressFormDialog } from './ClientAddressFormDialog';
import type { ClientAddressDto, ClientAddressListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'cep',
  sortOrder: 'asc',
};

export interface ClientAddressesManagerProps {
  clientId: number;
}

export function ClientAddressesManager({ clientId }: ClientAddressesManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('cliente-enderecos', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<ClientAddressDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<ClientAddressListItem | null>(null);

  const hooks = useClientAddressesHooks(clientId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: ClientAddressListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.id);
      toast.success(t('features.clientes.enderecos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<ClientAddressListItem>[] = [
    {
      field: 'cep',
      headerName: t('features.clientes.enderecos.columns.cep'),
      width: 120,
      valueFormatter: (value: string) => formatCep(value),
    },
    {
      field: 'logradouro',
      headerName: t('features.clientes.enderecos.columns.logradouro'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        `${params.row.logradouro}, ${params.row.numero}${
          params.row.complemento ? ` - ${params.row.complemento}` : ''
        }`,
    },
    {
      field: 'bairro',
      headerName: t('features.clientes.enderecos.columns.bairro'),
      width: 160,
      sortable: false,
    },
    {
      field: 'cidade',
      headerName: t('features.clientes.enderecos.columns.cidade'),
      width: 160,
      sortable: false,
      renderCell: (params) => `${params.row.cidade} - ${params.row.uf}`,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.enderecos.actions.edit', { id: params.row.id })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.clientes.enderecos.actions.delete', { id: params.row.id })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.clientes.enderecos.actions.new')}
        </Button>
      </Stack>

      <DataTable<ClientAddressListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.clientes.enderecos.searchPlaceholder')}
      />

      <ClientAddressFormDialog
        open={editing !== null}
        address={editing === 'new' ? null : editing}
        hooks={{
          useCreate: hooks.useCreate,
          useUpdate: hooks.useUpdate,
          useLookupCep: hooks.useLookupCep,
        }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.clientes.enderecos.deleteConfirm.title')}
        description={t('features.clientes.enderecos.deleteConfirm.description', {
          endereco: pendingDelete
            ? `${pendingDelete.logradouro}, ${pendingDelete.numero} - ${pendingDelete.cidade}/${pendingDelete.uf}`
            : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
